import cx from 'classnames';
import { HeaderPage } from 'components/header-page/header-page';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { Button } from 'components/ui/Button';

import { DocumentHeaderInfo } from 'components/document-header-info';
import { Loader } from 'components/loader/loader';
import { Formik, FormikValues } from 'formik';
import { useGetCoreDocumentsQuery } from 'store/api/documents';
import { prepareDate } from 'utils/date';
import {
  SchemeModificators,
  generateForm,
  generateInitialValues,
} from 'utils/sheme-parser-v2/generate-form';

import { getCurrentLocale } from 'utils/locale';
import { AppRoutes } from 'config/routes';
import { useCallback, useEffect } from 'react';
import styles from './NewRequest.module.scss';
import { useNewRequest } from './use-new-request';

const FormResetter = ({
  resetForm,
  setStep,
  currentSelect,
}: {
  resetForm: () => void;
  setStep: (step: number) => void;
  currentSelect: { value: string; label: string };
}) => {
  useEffect(() => {
    resetForm();
    setStep(1);
  }, [currentSelect]);

  return null;
};

export const NewRequestPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useTranslation();

  const withHeader = true;
  const withFooter = true;

  const {
    step,
    steps,
    model,
    isLoading,
    setStep,
    handleCreate,
    currentScheme,
    handleSign,
    handleClose,
    isApproval,
    getValidationSchema,
  } = useNewRequest();

  const { data: [document] = [] } = useGetCoreDocumentsQuery(
    { ids: [id ?? ''] },
    { skip: !id },
  );

  const lang = getCurrentLocale()

  const onSuccess = useCallback(() => {
    navigate(AppRoutes.Dashboard.Home + AppRoutes.Dashboard.CustomWidget.Home);
  }, [navigate]);

  const handlePrevStep = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const onSubmit = async (values: FormikValues) => {
    if (step < steps?.length) {
      setStep(step + 1);
      return;
    }

    if (isApproval && id) {
      handleSign(id);
      return;
    }

    const modifiedValues = Object.entries(values).reduce((acc, [key, value]) => {
      if (value !== null && value !== undefined) {
        acc[key] = value;

        if ((model as any)[key].type === 'datepicker') {
          acc[key] = prepareDate(value);
        }

        if ((model as any)[key].type === 'checkbox') {
          acc[key] = value?.toString();
        }
      }
      return acc;
    }, {} as any);

    if (currentScheme) {
      await handleCreate(modifiedValues);
    }
  };

  if (!currentScheme) {
    return <Loader size="medium" />;
  }

  return (
    <Formik
      enableReinitialize
      onSubmit={onSubmit}
      validationSchema={getValidationSchema()}
      initialValues={generateInitialValues(model)}
    >
      {({ submitForm, resetForm, values, errors }) => (
        <div className={styles.container}>
          <div className={styles.innerContainer}>
            <FormResetter
              currentSelect={currentScheme}
              resetForm={resetForm}
              setStep={setStep}
            />
            {withHeader && !isApproval && (
              <HeaderPage title={t(id ? 'Корректировка' : 'New item')} />
            )}
            {withHeader && isApproval && <DocumentHeaderInfo isLongTitle document={document} />}

            <div className={styles.contentBox}>
              <div className={styles.card}>
                {generateForm({
                  model,
                  step,
                  formValues: values,
                  modificators: isApproval ? [SchemeModificators.disabled] : ([] as any),
                })}
              </div>
            </div>

            {withFooter && (
              <div className={styles['footer']}>
                {step > 1 && (
                  <Button
                    className={cx(styles['button'], styles['button-back'])}
                    onClick={handlePrevStep}
                  >
                    {t('Back')}
                  </Button>
                )}

                <Button
                  displayType="outlined-tetriary"
                  className={cx(styles['button'])}
                  onClick={handleClose}
                >
                  {t('Cancel')}
                </Button>
                {isApproval && (
                  <Button
                    displayType="outlined-primary"
                    className={cx(styles['button'])}
                    onClick={() => {
                      // send to correct
                    }}
                  >
                    {t('Вернуть на корректировку')}
                  </Button>
                )}

                {currentScheme?.isNeedToSign && (
                  <Button className={cx(styles['button'])} onClick={submitForm}>
                    {isLoading
                      ? t('Loading...')
                      : t(isApproval ? 'Согласовать' : 'Отправить на согласование')}
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </Formik>
  );
};
